import {
  ApplicationIcon,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  Typography,
} from "@saviynt/design-system";
import { ReactNode, useEffect } from "react";

interface ModalFullPageProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  icon?: string;
  children: ReactNode;
  className?: string;
  closeOnEsc?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    isLoading?: boolean;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
}

export default function ModalFullPage({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  className = "",
  closeOnEsc = false,
  primaryAction,
  secondaryAction,
}: ModalFullPageProps) {
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed top-0 left-0 w-screen h-screen z-[9999] bg-[var(--color-background-neutral-subtlest)] flex flex-col ${className}`}
    >
      {title && (
        <div
          className="flex items-center justify-between px-6 py-4 bg-[var(--color-background-neutral-subtlest)] border-b"
          style={{
            borderBottomColor:
              "var(--color-border-secondary-subtlest, #293950)",
          }}
        >
          <div className="flex flex-col gap-2">
            <Typography kind="h1">{title}</Typography>
            {subtitle && icon && (
              <div className="flex items-center gap-2">
                <ApplicationIcon logo={icon} size="xSmall" />
                <Typography kind="body1" className="font-medium">
                  {subtitle}
                </Typography>
              </div>
            )}
          </div>
          <ButtonIcon
            size="medium"
            kind="ghost"
            icon={<Icon kind="Close" size="smallMedium" />}
            onClick={() => onClose()}
            ariaLabel="Close modal"
          />
        </div>
      )}
      <div className="flex-1 overflow-auto bg-[var(--color-background-neutral-subtlest)]">
        {children}
      </div>
      {(primaryAction || secondaryAction) && (
        <FooterActionBar size="large">
          <div className="flex justify-end gap-2 w-full">
            {secondaryAction && (
              <Button
                kind="outlined"
                size="medium"
                onClick={secondaryAction.onClick}
                isDisabled={secondaryAction.disabled}
              >
                {secondaryAction.label}
              </Button>
            )}
            {primaryAction && (
              <Button
                kind="filled"
                size="medium"
                onClick={primaryAction.onClick}
                isDisabled={primaryAction.disabled}
                isLoading={primaryAction.isLoading}
              >
                {primaryAction.label}
              </Button>
            )}
          </div>
        </FooterActionBar>
      )}
    </div>
  );
}
